'use client';

import type { QueryParams } from '@/lib/api/httpClient';
import { createHttpClient } from '@/lib/api/httpClient';
import {
  mapBlockchain,
  mapBonusBurnPreview,
  mapCoin,
  mapDepositAddress,
  mapInitiateWithdrawResult,
  mapTopHoldersResponse,
  mapTransaction,
  mapWithdrawSettings,
} from '@/lib/api/transaction/mappers';
import type {
  Blockchain,
  BonusBurnPreview,
  Coin,
  DepositAddress,
  InitiateWithdrawResult,
  TopHoldersResponse,
  Transaction,
  WithdrawSettings,
} from '@/lib/api/transaction/models';
import type {
  BlockchainDto,
  BonusBurnPreviewDto,
  CoinDto,
  DepositAddressRequestDto,
  DepositAddressResponseDto,
  InitiateWithdrawRequestDto,
  InitiateWithdrawResponseDto,
  SubmitWithdrawRequestDto,
  TopHoldersQueryParams,
  TopHoldersResponseDto,
  TransactionListResponse,
  TransactionsQueryParams,
  WithdrawSettingsDto,
} from '@/lib/api/transaction/types';

const BASE_URL = process.env.NEXT_PUBLIC_API_WALLET_URL;

// Cookie-based wallet client — auth rides the HttpOnly cookie
const walletHttp = createHttpClient({ baseUrl: BASE_URL });

export interface TransactionsResult {
  transactions: Transaction[];
  page: number;
  size: number;
  totalPages: number;
}

export async function fetchTransactions(
  params?: TransactionsQueryParams,
): Promise<TransactionsResult> {
  const response = await walletHttp.get<TransactionListResponse>('/transactions', {
    query: (params as unknown as QueryParams) ?? undefined,
  });

  if (!response) {
    return {
      transactions: [],
      page: 0,
      size: 0,
      totalPages: 0,
    };
  }

  return {
    transactions: (response.i ?? []).map(mapTransaction),
    page: response.p ?? 0,
    size: response.s ?? 0,
    totalPages: response.tp ?? 0,
  };
}

export async function fetchCoins(): Promise<Coin[]> {
  const response = await walletHttp.get<CoinDto[]>('/coins');

  if (!response || !Array.isArray(response)) {
    return [];
  }

  return response.map(mapCoin);
}

export async function fetchTopHolders(
  params: TopHoldersQueryParams,
): Promise<TopHoldersResponse | null> {
  const http = createHttpClient({ baseUrl: BASE_URL });
  const response = await http.get<TopHoldersResponseDto>('/user-balances/top-holders', {
    query: (params as unknown as QueryParams) ?? undefined,
  });

  if (!response) {
    return null;
  }

  return mapTopHoldersResponse(response);
}

export async function fetchBlockchains(coinId: string): Promise<Blockchain[]> {
  const http = createHttpClient({ baseUrl: BASE_URL });
  const response = await http.get<BlockchainDto[]>('/blockchains', {
    query: { coinId },
  });

  if (!response || !Array.isArray(response)) {
    return [];
  }

  return response.map(mapBlockchain);
}

/*
 * Withdraw endpoints
 */

export async function fetchWithdrawSettings(
  coinId: string,
  blockchainId: string,
): Promise<WithdrawSettings> {
  const response = await walletHttp.get<WithdrawSettingsDto>('/withdraw/settings', {
    query: { coinId, blockchainId },
  });

  return mapWithdrawSettings(response);
}

export async function fetchBonusBurnPreview(amountUsd: number): Promise<BonusBurnPreview> {
  const response = await walletHttp.get<BonusBurnPreviewDto>('/withdraw/bonus-burn-preview', {
    query: { amountUsd },
  });

  return mapBonusBurnPreview(response);
}

export async function initiateWithdraw(
  payload: InitiateWithdrawRequestDto,
): Promise<InitiateWithdrawResult> {
  const response = await walletHttp.post<InitiateWithdrawResponseDto>('/withdraw/initiate', {
    body: payload,
  });

  return mapInitiateWithdrawResult(response);
}

export async function submitWithdraw(
  transactionId: string,
  payload: SubmitWithdrawRequestDto,
): Promise<void> {
  await walletHttp.post<void>(`/withdraw/submit/${transactionId}`, { body: payload });
}

export async function cancelWithdraw(transactionId: string): Promise<void> {
  await walletHttp.post<void>(`/withdraw/cancel/${transactionId}`);
}

export async function resendWithdrawCode(transactionId: string): Promise<void> {
  await walletHttp.post<void>(`/withdraw/resend/${transactionId}`);
}

/*
 * Deposit endpoints — separate address-service
 */

const ADDRESS_SERVICE_URL = process.env.NEXT_PUBLIC_API_ADDRESS_SERVICE_URL;

const addressHttp = createHttpClient({ baseUrl: ADDRESS_SERVICE_URL });

export async function createDepositAddress(
  payload: DepositAddressRequestDto,
): Promise<DepositAddress> {
  const response = await addressHttp.post<DepositAddressResponseDto>('/api/v1/deposit-address', {
    body: payload,
  });

  return mapDepositAddress(response);
}

export async function fetchDepositAddress(
  coinId: string,
  blockchainId: string,
): Promise<DepositAddress> {
  const response = await addressHttp.get<DepositAddressResponseDto>('/api/v1/deposit-address', {
    query: { c: coinId, b: blockchainId },
  });

  return mapDepositAddress(response);
}
